/**
 * Goal 2: rectify/sequel report as one dialog-owned operation.
 * Checklist failure after sequel created → PARTIAL.
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  canConfirmStepFromEvidence,
} from "../../../../operationFeedback";

export const RECTIFY_REPORT_OPERATION_KEY = "rectify_report";

export function evaluateRectifyReportPreflight(values, report) {
  if (!(values?.id || report?.id)) {
    return { ready: false, reason: "missing_id" };
  }
  if (report?.sequelReport?.id) {
    return { ready: false, reason: "already_has_sequel" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t, withChecklist) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.rectify_report.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "create_sequel",
      label: t("operation_feedback.rectify_report.step.create_sequel"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "checklist",
      label: t("operation_feedback.rectify_report.step.checklist"),
      status: withChecklist ? STEP_STATUS.PENDING : STEP_STATUS.NOT_RELEVANT,
    },
  ];
}

/**
 * @param {object} args
 * @param {Function} args.dispatch
 * @param {Function} args.updateReportAsync
 * @param {object} args.values
 * @param {object} [args.report]
 * @param {string|number} [args.companyId]
 * @param {Function} [args.afterSequel]
 * @param {boolean} [args.hasChecklistStep]
 * @param {Function} [args.onCreated]
 * @param {Function} args.t
 */
export async function runRectifyReportOperation({
  dispatch,
  updateReportAsync,
  values,
  report,
  companyId,
  afterSequel,
  hasChecklistStep = false,
  onCreated,
  t,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = values?.id || report?.id;
  const title = t("operation_feedback.rectify_report.title", {
    number: report?.report_number || report?.displayTitle || resourceId || "",
  });

  const op = coordinator.start({
    operationKey: RECTIFY_REPORT_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t, hasChecklistStep),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateRectifyReportPreflight(values, report);
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional */
  }

  if (!preflight.ready) {
    const detail =
      preflight.reason === "already_has_sequel"
        ? t("operation_feedback.rectify_report.preflight_has_sequel")
        : t("operation_feedback.rectify_report.preflight_failed");
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "create_sequel", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.rectify_report.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "create_sequel", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let action;
  try {
    action = await dispatch(updateReportAsync(values));
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: RECTIFY_REPORT_OPERATION_KEY,
    });
    coordinator.updateStep(id, "create_sequel", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = action?.payload;
  const rejected = action?.meta?.requestStatus === "rejected";
  if (
    rejected ||
    !payload?.id ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true
  ) {
    const normalized = normalizeError(
      rejected
        ? { response: { status: payload?.response?.status, data: payload } }
        : { response: { status: 422, data: payload } },
      { operationKey: RECTIFY_REPORT_OPERATION_KEY }
    );
    coordinator.updateStep(id, "create_sequel", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      error: {
        safeMessage: normalized.safeMessage,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  const links = [
    {
      href: `/dashboard/service/reports/${payload.id}`,
      label: t("operation_feedback.rectify_report.open_report", {
        id: payload.id,
      }),
    },
  ];

  coordinator.updateStep(id, "create_sequel", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.rectify_report.create_ok"),
    resultRef: { id: payload.id },
    revision: rev++,
  });

  let checklistFailed = false;
  if (hasChecklistStep && typeof afterSequel === "function") {
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await afterSequel(payload);
      coordinator.updateStep(id, "checklist", {
        status: STEP_STATUS.COMPLETED,
        detail: t("operation_feedback.rectify_report.checklist_ok"),
        revision: rev++,
      });
    } catch (checkErr) {
      checklistFailed = true;
      const normalized = normalizeError(checkErr, {
        operationKey: RECTIFY_REPORT_OPERATION_KEY,
      });
      coordinator.updateStep(id, "checklist", {
        status: STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
    }
  } else {
    coordinator.updateStep(id, "checklist", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
  }

  if (typeof onCreated === "function") {
    try {
      onCreated(payload);
    } catch (navErr) {
      console.error(navErr);
    }
  }

  coordinator.complete(id, {
    summary: checklistFailed
      ? t("operation_feedback.rectify_report.partial_checklist")
      : t("operation_feedback.rectify_report.success"),
    outcome: checklistFailed ? OUTCOME.PARTIAL : OUTCOME.APPLIED,
    links,
  });
  return {
    clientOperationId: id,
    ok: true,
    payload,
    checklistFailed,
  };
}
